import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { formatPrice } from "../utils/helpers";
import { getSelectedOptionsKey, getSelectedOptionsList } from "../utils/productOptions";
import { motion } from "framer-motion";
import api from "../utils/api";
import { normalizePaymentMethods } from "../utils/paymentMethods";
import { normalizeShippingMethods } from "../utils/shippingMethods";

const DEFAULT_MIN_ORDER_VALUE = 200;
const DEFAULT_SHIPPING_COUNTRIES = ["Ireland", "Northern Ireland", "United Kingdom"];
const SUPPORTED_PAYMENT_METHOD_IDS = ["paypal", "mypos", "30_day_credit"];

const slugifyPaymentValue = (value) => String(value || "")
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "_")
  .replace(/^_+|_+$/g, "");

const getPaymentProcessorId = (method) => {
  const id = slugifyPaymentValue(method?.id);
  const name = slugifyPaymentValue(method?.name);

  // Explicit checks for payment methods
  if (id === "paypal" || name === "paypal") {
    return "paypal";
  }

  if (id === "mypos" || name === "mypos") {
    return "mypos";
  }

  // Check for 30 day credit / account credit / add to account payment method
  // Matches: "30 day credit", "30_day_credit", "account", "add_to_account", "add to account", etc.
  if (
    [id, name].includes("30_day_credit") || 
    [id, name].includes("30daycredit") ||
    (name.includes("day") && name.includes("credit")) ||
    (name.includes("account") && (name.includes("credit") || name.includes("day"))) ||
    (name.includes("add") && name.includes("account")) ||
    (name.includes("add") && name.includes("credit")) ||
    id.includes("30_day") || id.includes("account")
  ) {
    return "30_day_credit";
  }

  return id || name;
};

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, clearCart } = useCart();
  const { user, isAuthenticated, isApproved, checkSession } = useAuth();
  const { exchangeRates, selectedCurrency } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [sameBillingAddress, setSameBillingAddress] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState("paypal");
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [shippingCountries, setShippingCountries] = useState(DEFAULT_SHIPPING_COUNTRIES);
  const [shippingMethods, setShippingMethods] = useState([]);
  const [selectedShippingMethodId, setSelectedShippingMethodId] = useState("");
  const [minimumOrderValue, setMinimumOrderValue] = useState(DEFAULT_MIN_ORDER_VALUE);

  const [shippingForm, setShippingForm] = useState({
    firstName: "",
    lastName: "",
    company: "",
    address: "",
    city: "",
    postalCode: "",
    country: DEFAULT_SHIPPING_COUNTRIES[0],
    phone: "",
    email: user?.email || "",
  });

  const [billingForm, setBillingForm] = useState({
    firstName: "",
    lastName: "",
    company: "",
    address: "",
    city: "",
    postalCode: "",
    country: DEFAULT_SHIPPING_COUNTRIES[0],
  });

  const [customerNotes, setCustomerNotes] = useState("");

  useEffect(() => {
    checkSession();

    const loadCheckoutSettings = async () => {
      try {
        const [{ data }, profileResponse] = await Promise.all([
          api.get("/settings/checkout"),
          api.get("/auth/profile").catch(() => ({ data: { user: null } })),
        ]);
        const countries = data.settings?.shipping_countries?.length ? data.settings.shipping_countries : DEFAULT_SHIPPING_COUNTRIES;
        const minOrderValue = Number(data.settings?.min_order_value ?? DEFAULT_MIN_ORDER_VALUE);
        const nextPaymentMethods = normalizePaymentMethods(data.settings?.payment_methods || []);
        const methods = normalizeShippingMethods(data.settings?.shipping_methods || []);
        const profile = profileResponse.data?.user || null;

        setPaymentMethods(nextPaymentMethods);
        setShippingCountries(countries);
        setShippingMethods(methods);
        setMinimumOrderValue(Number.isFinite(minOrderValue) ? minOrderValue : DEFAULT_MIN_ORDER_VALUE);
        setShippingForm((current) => ({
          ...current,
          firstName: profile?.first_name || current.firstName,
          lastName: profile?.last_name || current.lastName,
          address: profile?.address_line1 || current.address,
          city: profile?.city || current.city,
          postalCode: profile?.postal_code || current.postalCode,
          country: countries.includes(profile?.country)
            ? profile.country
            : countries.includes(current.country)
              ? current.country
              : countries[0],
          phone: profile?.phone || current.phone,
          email: profile?.email || current.email,
        }));
        setBillingForm((current) => ({
          ...current,
          firstName: profile?.first_name || current.firstName,
          lastName: profile?.last_name || current.lastName,
          address: profile?.billing_address_line1 || profile?.address_line1 || current.address,
          city: profile?.billing_city || profile?.city || current.city,
          postalCode: profile?.billing_postal_code || profile?.postal_code || current.postalCode,
          country: countries.includes(profile?.billing_country)
            ? profile.billing_country
            : countries.includes(profile?.country)
              ? profile.country
            : countries.includes(current.country)
              ? current.country
              : countries[0],
        }));
      } catch (error) {
        console.error("Error fetching checkout settings:", error);
      }
    };

    loadCheckoutSettings();
  }, [checkSession]);

  useEffect(() => {
    if (paymentMethods.length === 0) {
      setPaymentMethod("");
      return;
    }

    if (!paymentMethods.some((method) => method.id === paymentMethod)) {
      setPaymentMethod(paymentMethods[0]?.id || "");
    }
  }, [paymentMethods, paymentMethod]);

  useEffect(() => {
    if (shippingMethods.length === 0) {
      setSelectedShippingMethodId("");
      return;
    }

    const currentAvailableMethod = shippingMethods.find((method) => method.id === selectedShippingMethodId);
    if (!currentAvailableMethod) {
      setSelectedShippingMethodId(shippingMethods[0]?.id || "");
    }
  }, [shippingMethods, selectedShippingMethodId]);

  useEffect(() => {
    if (!cart || cart.length === 0) {
      toast.info("Your cart is empty");
      navigate("/cart");
      return;
    }

    if (!isAuthenticated()) {
      toast.error("Please login as an approved trade customer to checkout");
      navigate("/login");
      return;
    }

    if (user?.role !== "trade_customer" || !isApproved()) {
      toast.error("Checkout is available for approved trade customers only");
      navigate("/login");
    }
  }, [cart, user, navigate, isAuthenticated, isApproved]);

  const getAvailableShippingMethods = () => {
    if (!cart.length || !shippingMethods.length) {
      return [];
    }

    const allowedIds = cart.reduce((availableIds, item, index) => {
      const productMethodIds = Array.isArray(item.product_shipping_method_ids)
        ? item.product_shipping_method_ids.map((methodId) => String(methodId))
        : [];

      if (index === 0) {
        return productMethodIds;
      }

      return availableIds.filter((methodId) => productMethodIds.includes(methodId));
    }, []);

    const userAllowedIds = Array.isArray(user?.allowed_shipping_method_ids) ? user.allowed_shipping_method_ids.map((methodId) => String(methodId)) : [];
    const effectiveAllowedIds = allowedIds.filter((methodId) => userAllowedIds.includes(methodId));

    return shippingMethods.filter((method) => effectiveAllowedIds.includes(method.id));
  };

  const getAvailablePaymentMethods = () => {
    if (!cart.length || !paymentMethods.length) {
      return [];
    }

    const allPaymentMethodIds = paymentMethods.map((method) => method.id);

    const allowedIds = cart.reduce((availableIds, item, index) => {
      const configuredProductMethodIds = Array.isArray(item.product_payment_method_ids)
        ? item.product_payment_method_ids.map((methodId) => String(methodId))
        : [];
      const productMethodIds = configuredProductMethodIds.length > 0 ? configuredProductMethodIds : allPaymentMethodIds;

      if (index === 0) {
        return productMethodIds;
      }

      return availableIds.filter((methodId) => productMethodIds.includes(methodId));
    }, []);

    const userAllowedIds = Array.isArray(user?.allowed_payment_method_ids) ? user.allowed_payment_method_ids.map((methodId) => String(methodId)) : [];
    const effectiveAllowedIds = allowedIds.filter((methodId) => userAllowedIds.includes(methodId));

    return paymentMethods.filter((method) => effectiveAllowedIds.includes(method.id));
  };

  const availableShippingMethods = getAvailableShippingMethods();
  const availablePaymentMethods = getAvailablePaymentMethods();
  const selectablePaymentMethods = availablePaymentMethods.filter((method) => SUPPORTED_PAYMENT_METHOD_IDS.includes(getPaymentProcessorId(method)));
  const selectedPaymentMethodConfig = availablePaymentMethods.find((method) => method.id === paymentMethod) || null;
  const selectedPaymentProcessorId = getPaymentProcessorId(selectedPaymentMethodConfig);
  const selectedShippingMethod = availableShippingMethods.find((method) => method.id === selectedShippingMethodId) || null;
  useEffect(() => {
    if (availableShippingMethods.length === 0) {
      if (selectedShippingMethodId) {
        setSelectedShippingMethodId("");
      }
      return;
    }

    if (!availableShippingMethods.some((method) => method.id === selectedShippingMethodId)) {
      setSelectedShippingMethodId(availableShippingMethods[0].id);
    }
  }, [availableShippingMethods, selectedShippingMethodId]);

  useEffect(() => {
    if (selectablePaymentMethods.length === 0) {
      if (paymentMethod) {
        setPaymentMethod("");
      }
      return;
    }

    if (!selectablePaymentMethods.some((method) => method.id === paymentMethod)) {
      setPaymentMethod(selectablePaymentMethods[0].id);
    }
  }, [selectablePaymentMethods, paymentMethod]);

  const subtotal = cart.reduce((sum, item) => sum + Number(item.price || 0) * item.quantity, 0);
  const tax = 0; // Calculate if needed
  const shipping = Number(selectedShippingMethod?.price || 0);
  const total = subtotal + tax + shipping;
  const meetsMinimumOrder = total >= minimumOrderValue;

  const handleShippingChange = (e) => {
    setShippingForm({ ...shippingForm, [e.target.name]: e.target.value });
  };

  const handleBillingChange = (e) => {
    setBillingForm({ ...billingForm, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    if (!meetsMinimumOrder) {
      toast.error(`Minimum order value is ${formatPrice(minimumOrderValue, selectedCurrency, exchangeRates)}`);
      return false;
    }

    const required = ["firstName", "lastName", "address", "city", "postalCode", "country", "phone", "email"];
    for (const field of required) {
      if (!shippingForm[field]) {
        toast.error(`Please fill in ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
        return false;
      }
    }

    if (!sameBillingAddress) {
      const billingRequired = ["firstName", "lastName", "address", "city", "postalCode", "country"];
      for (const field of billingRequired) {
        if (!billingForm[field]) {
          toast.error(`Please fill in billing ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
          return false;
        }
      }
    }

    if (!selectedShippingMethodId || !selectedShippingMethod) {
      toast.error("Please select a shipping method");
      return false;
    }

    if (!paymentMethod || !selectablePaymentMethods.some((method) => method.id === paymentMethod)) {
      toast.error("Please select a payment method");
      return false;
    }

    return true;
  };

  const createOrder = async () => {
    if (!validateForm()) return null;

    setLoading(true);

    try {
      const response = await api.post('/orders', {
        shippingAddress: shippingForm,
        billingAddress: sameBillingAddress ? shippingForm : billingForm,
        shippingMethodId: selectedShippingMethodId,
        paymentMethod,
        customerNotes,
        sameBillingAddress,
      });

      return response.data.order;
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error(error.response?.data?.error || 'Failed to create order');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handlePayPalPayment = async () => {
    setProcessingPayment(true);

    try {
      // Create order first
      const order = await createOrder();
      if (!order) {
        setProcessingPayment(false);
        return;
      }

      // Create PayPal payment
      const paypalResponse = await api.post(`/orders/${order.id}/paypal/create`, {});

      const { approvalUrl, paypalOrderId } = paypalResponse.data;

      if (!approvalUrl || !paypalOrderId) {
        throw new Error("PayPal approval link was not created");
      }

      window.location.href = approvalUrl;
    } catch (error) {
      console.error('PayPal payment error:', error);
      toast.error(error.response?.data?.error || 'PayPal payment failed');
      setProcessingPayment(false);
    }
  };

  const handleMyPosPayment = async () => {
    setProcessingPayment(true);

    try {
      const order = await createOrder();
      if (!order) {
        setProcessingPayment(false);
        return;
      }

      const myposResponse = await api.post(`/orders/${order.id}/mypos/create`, {});
      const { redirectUrl } = myposResponse.data;

      if (!redirectUrl) {
        throw new Error("MyPos redirect URL was not created");
      }

      window.location.href = redirectUrl;
    } catch (error) {
      console.error("MyPos payment error:", error);
      toast.error(error.response?.data?.error || "MyPos payment failed");
      setProcessingPayment(false);
    }
  };

  const handleThirtyDayCreditPayment = async () => {
    setProcessingPayment(true);

    try {
      const order = await createOrder();
      if (!order) {
        setProcessingPayment(false);
        return;
      }

      await clearCart();
      toast.success("Order placed on 30 day credit");
      navigate("/orders");
    } catch (error) {
      console.error("30 day credit checkout error:", error);
      toast.error(error.response?.data?.error || "30 day credit checkout failed");
      setProcessingPayment(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedPaymentProcessorId === 'paypal') {
      await handlePayPalPayment();
      return;
    }

    if (selectedPaymentProcessorId === 'mypos') {
      await handleMyPosPayment();
      return;
    }

    if (selectedPaymentProcessorId === '30_day_credit') {
      await handleThirtyDayCreditPayment();
    }
  };

  return (
    <div className="min-h-screen bg-black py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="heading-metallic mb-2 text-3xl sm:text-4xl">Checkout</h1>
          <p className="text-gray-400">Complete your order</p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Shipping Address */}
              <div className="bg-gradient-to-r from-black via-gray-900 to-black p-6 rounded-lg border border-gray-800">
                <h2 className="heading-metallic-soft text-2xl mb-6">Shipping Address</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={shippingForm.firstName}
                      onChange={handleShippingChange}
                      className="w-full bg-black border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={shippingForm.lastName}
                      onChange={handleShippingChange}
                      className="w-full bg-black border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={shippingForm.company}
                      onChange={handleShippingChange}
                      className="w-full bg-black border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Eircode *
                    </label>
                    <input
                      type="text"
                      name="postalCode"
                      value={shippingForm.postalCode}
                      onChange={handleShippingChange}
                      className="w-full bg-black border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold"
                      placeholder="Enter Eircode"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Country *
                    </label>
                    <select
                      name="country"
                      value={shippingForm.country}
                      onChange={handleShippingChange}
                      className="w-full bg-black border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold"
                      required
                    >
                      {shippingCountries.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Address *
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={shippingForm.address}
                      onChange={handleShippingChange}
                      className="w-full bg-black border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={shippingForm.city}
                      onChange={handleShippingChange}
                      className="w-full bg-black border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={shippingForm.phone}
                      onChange={handleShippingChange}
                      className="w-full bg-black border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={shippingForm.email}
                      onChange={handleShippingChange}
                      className="w-full bg-black border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Billing Address */}
              <div className="bg-gradient-to-r from-black via-gray-900 to-black p-6 rounded-lg border border-gray-800">
                <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <h2 className="heading-metallic-soft text-2xl">Billing Address</h2>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sameBillingAddress}
                      onChange={(e) => setSameBillingAddress(e.target.checked)}
                      className="mr-2 h-4 w-4 text-gold focus:ring-gold border-gray-700 rounded"
                    />
                    <span className="text-sm text-gray-300">Same as shipping</span>
                  </label>
                </div>

                {!sameBillingAddress && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={billingForm.firstName}
                        onChange={handleBillingChange}
                        className="w-full bg-black border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={billingForm.lastName}
                        onChange={handleBillingChange}
                        className="w-full bg-black border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold"
                        required
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={billingForm.company}
                        onChange={handleBillingChange}
                        className="w-full bg-black border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Address *
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={billingForm.address}
                        onChange={handleBillingChange}
                        className="w-full bg-black border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        City *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={billingForm.city}
                        onChange={handleBillingChange}
                        className="w-full bg-black border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Postal Code *
                      </label>
                      <input
                        type="text"
                        name="postalCode"
                        value={billingForm.postalCode}
                        onChange={handleBillingChange}
                        className="w-full bg-black border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold"
                        required
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Country *
                      </label>
                      <select
                        name="country"
                        value={billingForm.country}
                        onChange={handleBillingChange}
                        className="w-full bg-black border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold"
                        required
                      >
                        {shippingCountries.map((country) => (
                          <option key={country} value={country}>
                            {country}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-gradient-to-r from-black via-gray-900 to-black p-6 rounded-lg border border-gray-800">
                <h2 className="heading-metallic-soft text-2xl mb-6">Shipping Method</h2>

                {availableShippingMethods.length === 0 ? (
                  <div className="rounded-lg border border-yellow-700 bg-yellow-950 px-4 py-4 text-sm text-yellow-200">
                    No shipping method is approved for your account and every product in your cart.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {availableShippingMethods.map((method) => (
                      <label key={method.id} className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-700 p-4 hover:border-gold transition">
                        <input
                          type="radio"
                          name="shippingMethod"
                          value={method.id}
                          checked={selectedShippingMethodId === method.id}
                          onChange={(e) => setSelectedShippingMethodId(e.target.value)}
                          className="mt-1 h-4 w-4 text-gold focus:ring-gold"
                        />
                        <div className="flex-1">
                          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                            <span className="font-medium text-white">{method.name}</span>
                            <span className="text-gold">{formatPrice(Number(method.price), selectedCurrency, exchangeRates)}</span>
                          </div>
                          <p className="text-sm text-gray-400 mt-1">{method.duration}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Payment Method */}
              <div className="bg-gradient-to-r from-black via-gray-900 to-black p-6 rounded-lg border border-gray-800">
                <h2 className="heading-metallic-soft text-2xl mb-6">Payment Method</h2>

                {availablePaymentMethods.length === 0 ? (
                  <div className="rounded-lg border border-yellow-700 bg-yellow-950 px-4 py-4 text-sm text-yellow-200">
                    No payment method is approved for your account and the products currently in your cart.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {availablePaymentMethods.map((method) => (
                      <label
                        key={method.id}
                        className={`flex items-center p-4 border rounded-lg transition ${
                          SUPPORTED_PAYMENT_METHOD_IDS.includes(getPaymentProcessorId(method))
                            ? "border-gray-700 cursor-pointer hover:border-gold"
                            : "border-gray-800 cursor-not-allowed opacity-60"
                        }`}
                      >
                        <input
                          type="radio"
                          name="paymentMethod"
                          value={method.id}
                          checked={paymentMethod === method.id}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          disabled={!SUPPORTED_PAYMENT_METHOD_IDS.includes(getPaymentProcessorId(method))}
                          className="mr-3 h-4 w-4 text-gold focus:ring-gold"
                        />
                        <div className="flex-1">
                          <span className="text-white font-medium">{method.name}</span>
                          {getPaymentProcessorId(method) === "30_day_credit" && (
                            <p className="mt-1 text-xs text-gray-400">Place the order now and pay on your approved 30 day credit terms.</p>
                          )}
                          {!SUPPORTED_PAYMENT_METHOD_IDS.includes(getPaymentProcessorId(method)) && (
                            <p className="mt-1 text-xs text-gray-400">Not yet available to complete online checkout.</p>
                          )}
                        </div>
                      </label>
                    ))}
                    {selectablePaymentMethods.length === 0 && (
                      <div className="rounded-lg border border-yellow-700 bg-yellow-950 px-4 py-4 text-sm text-yellow-200">
                        Your account has payment methods assigned, but none of them are currently enabled for online checkout.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Order Notes */}
              <div className="bg-gradient-to-r from-black via-gray-900 to-black p-6 rounded-lg border border-gray-800">
                <h2 className="heading-metallic-soft text-2xl mb-6">Order Notes</h2>
                <textarea
                  name="customerNotes"
                  value={customerNotes}
                  onChange={(e) => setCustomerNotes(e.target.value)}
                  placeholder="Notes about your order, e.g., special delivery instructions..."
                  rows="4"
                  className="w-full bg-black border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-gold resize-none"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || processingPayment || availableShippingMethods.length === 0 || selectablePaymentMethods.length === 0}
                className="btn-primary w-full py-4"
              >
                {processingPayment ? 'Processing...' : selectedPaymentProcessorId === '30_day_credit' ? `Place Order ${formatPrice(total, selectedCurrency, exchangeRates)}` : `Pay ${formatPrice(total, selectedCurrency, exchangeRates)}`}
              </button>
              {selectedCurrency !== "EUR" && <p className="text-sm text-gray-400">PayPal charges are processed in EUR using your base order total.</p>}
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 rounded-lg border border-gray-800 bg-gradient-to-r from-black via-gray-900 to-black p-6 lg:top-24">
              <h2 className="heading-metallic-soft text-2xl mb-6">Order Summary</h2>
              
              <div className="space-y-4 mb-6">
                {cart.map((item) => (
                  <div key={item.id || `${item.product_id}-${getSelectedOptionsKey(item.selected_options)}`} className="flex items-start space-x-4">
                    {item.product_image ? (
                      <img
                        src={item.product_image?.startsWith("http") ? item.product_image : `${api.defaults.baseURL}${item.product_image}`}
                        alt={item.product_name}
                        className="w-16 h-16 object-cover rounded"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded bg-gray-800" />
                    )}
                    <div className="flex-1">
                      <p className="text-white font-medium text-sm">{item.product_name}</p>
                      <p className="text-gray-400 text-xs">Qty: {item.quantity}</p>
                      {getSelectedOptionsList(item.selected_options).map((option) => (
                        <p key={`${option.name}-${option.value}`} className="text-gray-500 text-xs">
                          {option.name}: {option.value}
                        </p>
                      ))}
                      <p className="text-gold text-sm">{formatPrice(Number(item.price) * item.quantity, selectedCurrency, exchangeRates)}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-700 pt-4 space-y-2">
                <div className="flex justify-between text-gray-300">
                  <span>Minimum order</span>
                  <span>{formatPrice(minimumOrderValue, selectedCurrency, exchangeRates)}</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Subtotal</span>
                  <span>{formatPrice(subtotal, selectedCurrency, exchangeRates)}</span>
                </div>
                {tax > 0 && (
                  <div className="flex justify-between text-gray-300">
                    <span>Tax</span>
                    <span>{formatPrice(tax, selectedCurrency, exchangeRates)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-300">
                  <span>Shipping</span>
                  <span>{selectedShippingMethod ? formatPrice(shipping, selectedCurrency, exchangeRates) : "Select a method"}</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-gold pt-2 border-t border-gray-700">
                  <span>Total</span>
                  <span>{formatPrice(total, selectedCurrency, exchangeRates)}</span>
                </div>
                {!meetsMinimumOrder && (
                  <div className="rounded-lg border border-yellow-700 bg-yellow-950 px-4 py-3 text-sm text-yellow-200">
                    Add {formatPrice(minimumOrderValue - total, selectedCurrency, exchangeRates)} more before checkout can continue.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
